import { useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, CheckCircle2, Database } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { formatDate, formatIsoDay, formatPct, formatRate, formatTRY } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useRates } from "@/components/layout/CurrencyTicker";

function RatesTable({ data }) {
  return (
    <table className="erp-table" data-testid="rates-table">
      <thead><tr><th>Döviz</th><th className="text-right">Döviz Alış</th><th className="text-right">Döviz Satış</th><th className="text-right">Efektif Alış</th><th className="text-right">Efektif Satış</th></tr></thead>
      <tbody>
        {["USD", "EUR", "CNY"].map((c) => {
          const r = data.rates?.[c] || {};
          return (
            <tr key={c} data-testid={`rates-row-${c.toLowerCase()}`}>
              <td><span className="font-bold font-mono">{c}</span> <span className="text-slate-400 text-xs">{r.name}</span></td>
              <td className="text-right font-mono">{formatRate(r.forex_buying)}</td>
              <td className="text-right font-mono font-semibold">{formatRate(r.forex_selling)}</td>
              <td className="text-right font-mono">{formatRate(r.banknote_buying)}</td>
              <td className="text-right font-mono">{formatRate(r.banknote_selling)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FormatCheck() {
  const [srv, setSrv] = useState(null);
  useEffect(() => { api.get("/meta/format-sample").then(({ data }) => setSrv(data)); }, []);
  const Row = ({ label, value, testid }) => (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-0 text-[13px]"><span className="text-slate-500">{label}</span><span className="font-mono font-semibold text-slate-900" data-testid={testid}>{value}</span></div>
  );
  return (
    <div className="panel p-4" data-testid="format-check">
      <h3 className="text-base font-semibold text-slate-800 mb-2">Biçim Kontrolü (tr-TR)</h3>
      <Row label="Arayüz · 1250000 TRY" value={formatTRY(1250000)} testid="format-try-frontend" />
      <Row label="Arayüz · 27,2 yüzde" value={formatPct(27.2)} testid="format-pct-frontend" />
      <Row label="API · 1250000 TRY" value={srv?.formatted_try || "…"} testid="format-try-backend" />
      <Row label="API · 27,2 yüzde" value={srv?.formatted_pct || "…"} testid="format-pct-backend" />
    </div>
  );
}

function SystemInfo() {
  const [sys, setSys] = useState(null);
  useEffect(() => { api.get("/meta/system").then(({ data }) => setSys(data)); }, []);
  return (
    <div className="panel p-4" data-testid="system-info">
      <h3 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><Database className="h-4 w-4" />Veri Tutarlılığı</h3>
      <div className="text-[13px] text-slate-600">Transaction modu: <span className="font-mono font-semibold text-slate-900" data-testid="system-tx-mode">{sys?.transaction_mode || "…"}</span></div>
      <p className="text-xs text-slate-400 mt-2">Çok dokümanlı işlemler MongoDB replica set transaction'ları ile yürütülür; oluşturma uçları ayrıca Idempotency-Key başlığını destekler.</p>
    </div>
  );
}

export default function RatesTab() {
  const { can } = useAuth();
  const [data, setData] = useRates();
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/currency/refresh");
      setData(r.rates);
      r.ok ? toast.success("TCMB kurları güncellendi") : toast.warning("TCMB'ye ulaşılamadı; son bilinen kur kullanılıyor");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!data) return <div className="panel h-48 animate-pulse" />;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="rates-tab">
      <div className="panel overflow-hidden xl:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-800">TCMB Gösterge Kurları <span className="text-xs text-slate-400 font-normal">· Baz: TRY</span></h3>
            <div className="text-xs text-slate-500" data-testid="rates-meta">Bülten {formatIsoDay(data.date)} {data.bulletin_no && `· ${data.bulletin_no}`} · Kaynak {data.source} · Alındı {formatDate(data.fetched_at)}</div>
          </div>
          <div className="flex items-center gap-2">
            {data.stale
              ? <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded bg-amber-100 text-amber-900 border border-amber-300" data-testid="rates-stale-badge"><AlertTriangle className="h-3.5 w-3.5" />Son bilinen kur</span>
              : <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="rates-live-badge"><CheckCircle2 className="h-3.5 w-3.5" />Güncel</span>}
            {can("ayarlar", "edit") && <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="rates-refresh-button"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />Kurları Yenile</Button>}
          </div>
        </div>
        {data.warning && <div className="px-4 py-2 text-xs bg-amber-50 text-amber-900 border-b border-amber-200" data-testid="rates-warning">{data.warning}</div>}
        <RatesTable data={data} />
      </div>
      <div className="space-y-4"><FormatCheck /><SystemInfo /></div>
    </div>
  );
}
